/**
 * FooterBar — one progress bar in the footer strip.
 * Lot B stub: numbers and fill only; lot G replaces the Prompts side.
 */
import { Box, Tooltip } from '@mui/material';
import type { AllowanceFigures } from '@traaker/shared';

export interface FooterBarProps {
  kind: 'prompts' | 'conversations';
  figures: AllowanceFigures | null;
  /** When true the bar turns amber */
  full?: boolean;
  onClick?: () => void;
  /** The numbers text, when the kind has its own (lot I3: « 68 / 200 runs », « 576 crédits par mois », « non configuré »). */
  nums?: string;
  /** No track (agency: credit-metered, no allowance to fill). */
  hideTrack?: boolean;
}

export function FooterBar({ kind, figures, full = false, onClick, nums, hideTrack = false }: FooterBarProps) {
  // i18n: promptStudio.foot.prompts / promptStudio.foot.conversations
  const label = kind === 'prompts' ? 'Prompts' : 'Conversations';

  const hasData = figures !== null;
  const used = figures?.used ?? 0;
  const allowance = figures?.allowance ?? -1;
  const unlimited = allowance === -1;
  const ratio = hasData && !unlimited && allowance > 0 ? Math.min(used / allowance, 1) : 0;
  const amber = full || (hasData && !unlimited && used >= allowance);

  // i18n: promptStudio.foot.barTitle
  const title = hasData
    ? nums
      ? `${label} : ${nums}`
      : unlimited
      ? `${label} : ${used}`
      : `${label} : ${used} / ${allowance}`
    : label;

  return (
    <Tooltip title={title} arrow>
      <Box
        component="button"
        onClick={onClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          border: '1px solid transparent',
          borderRadius: 1.5,
          px: 1.5,
          py: 0.75,
          cursor: onClick ? 'pointer' : 'default',
          bgcolor: 'transparent',
          '&:hover': onClick ? {
            bgcolor: 'rgba(124,58,237,.06)',
            borderColor: 'rgba(124,58,237,.2)',
          } : {},
        }}
      >
        <Box sx={{ fontSize: 11.5, fontWeight: 600, color: amber ? '#b45309' : 'text.secondary', whiteSpace: 'nowrap' }}>
          {label}
        </Box>
        {/* Track */}
        {!hideTrack && (
          <Box sx={{ position: 'relative', width: 112, height: 6, borderRadius: 3, bgcolor: '#e8e8f0', overflow: 'hidden' }}>
            {hasData && !unlimited && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${ratio * 100}%`,
                  bgcolor: amber ? '#f59e0b' : '#7c3aed',
                  borderRadius: 3,
                  transition: 'width .3s',
                }}
              />
            )}
          </Box>
        )}
        {hasData && (
          <Box sx={{ fontSize: 11.5, color: amber ? '#b45309' : 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            {nums ?? (unlimited ? `${used}` : `${used} / ${allowance}`)}
          </Box>
        )}
        {/* Caret */}
        <Box component="span" sx={{ fontSize: 10, color: 'text.disabled', ml: 0.25 }}>⌄</Box>
      </Box>
    </Tooltip>
  );
}
